import React, { useMemo } from 'react';
import { generatePath, Link } from 'react-router-dom';
import type { AppliedPromo, Product, ProductCartItem } from '../../../../../../types/common';
import IconButton from '../../../../../UI/IconButton/IconButton';
import FavoriteIcon from '../../../../../UI/icons/FavoriteIcon/FavoriteIcon';
import TrashIcon from '../../../../../UI/icons/TrashIcon/TrashIcon';
import QuantityBlock from '../../../../../UI/QuantityBlock/QuantityBlock';
import { toMoney } from '../../utils/money';
import './cartItemsSection.scss';

type ProductCartItemWithSecondPromo = ProductCartItem & {
    secondPillowAppliedQty?: number;
    secondPillowDiscountPercent?: number;
    secondPillowDiscountAmount?: number;
};
type Props = {
    items: ProductCartItemWithSecondPromo[];
    totalQuantity: number;
    selectedIds: string[];
    onToggleSelect: (id: string) => void;
    onSelectAll: () => void;
    onClearSelection: () => void;
    onRemoveSelected: () => void;
    onRemoveOne: (id: string) => void;
    onWish: ({ id, isWished }: { id: ProductCartItem['productId']; isWished: boolean }) => void;
    appliedPromo: AppliedPromo | null;
};

const getImageUrl = (imageId: string) => `${process.env.REACT_APP_SERVER_URL}/products/images/${imageId}`;
function normalizeGroupLine(group: Product['group'] | undefined): string | null {
    if (!group) return null;
    const url = typeof group.url === 'string' ? group.url : '';
    const optionValue = typeof group.optionValue === 'string' ? group.optionValue : '';
    const isRelevant = url === 'Размер' || url === 'Цвет' || url === 'Дизайн' || url === 'Дизайн/ Цвет';
    if (!isRelevant || !optionValue) return null;
    return `${url}: ${optionValue}`;
}
function readNameFromObj(v: unknown): string {
    if (!v || typeof v !== 'object') return '';
    const name = (v as Record<string, unknown>).name;
    return typeof name === 'string' ? name : '';
}
function pickFillerOrFabricName(item: unknown): string | null {
    if (!item || typeof item !== 'object') return null;
    const o = item as Record<string, unknown>;
    const fillerName = readNameFromObj(o.fillerType);
    const fabricName = readNameFromObj(o.fabricType);
    const v = (fillerName || fabricName).trim();
    return v ? v : null;
}

export const CartItemsSection: React.FC<Props> = ({
    items,
    totalQuantity,
    selectedIds,
    onToggleSelect,
    onSelectAll,
    onClearSelection,
    onRemoveSelected,
    onRemoveOne,
    onWish,
    appliedPromo,
}) => {
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const allSelected = items.length > 0 && items.every((item) => selectedSet.has(item.productId));

    // const baseTotalsSum = useMemo(() => {
    //     return items.reduce((acc, item) => acc + Number(item.price) * Number(item.quantity), 0);
    // }, [items]);
    function isPromoEligibleItem(item: ProductCartItemWithSecondPromo): boolean {
        const baseTotal = +(Number(item.price) * Number(item.quantity)).toFixed(2);
        const subtotalBeforePromo = typeof item.totalPrice === 'number' ? item.totalPrice : baseTotal;
        const hasNonPromoDiscountVisual =
            subtotalBeforePromo < baseTotal - 0.01 ||
            item.discountedPrice != null ||
            item.specialDiscountedPrice != null ||
            Number(item.secondPillowDiscountAmount || 0) > 0;
        return !hasNonPromoDiscountVisual;
    }
    const promoEligibleBaseTotalsSum = useMemo(() => {
        return items.reduce((acc, item) => {
            const baseTotal = +(Number(item.price) * Number(item.quantity)).toFixed(2);
            return isPromoEligibleItem(item) ? acc + baseTotal : acc;
        }, 0);
    }, [items]);

    return (
        <section className="cartItemsSection" aria-label="Товары в корзине">
            <div className="cartItemsSection__header">
                <div className="cartItemsSection__titleRow">
                    <h2 className="cartItemsSection__title">Корзина</h2>
                    <span className="cartItemsSection__count">{totalQuantity} шт</span>
                </div>

                <div className="cartItemsSection__actions">
                    <label className="cartItemsSection__selectAll">
                        <input type="checkbox" checked={allSelected} onChange={allSelected ? onClearSelection : onSelectAll} />
                        <span className="cartItemsSection__selectMark" aria-hidden="true" />

                        <button type="button" className="cartItemsSection__actionLink" onClick={allSelected ? onClearSelection : onSelectAll}>
                            {allSelected ? 'Снять выделение' : 'Выбрать все'}
                        </button>
                    </label>

                    <button
                        type="button"
                        className="cartItemsSection__actionLink cartItemsSection__actionLink--danger"
                        onClick={onRemoveSelected}
                        disabled={selectedIds.length === 0}
                    >
                        Удалить выбранное
                    </button>
                </div>
            </div>

            <div className="cartItemsSection__list" role="list">
                {items.map((item) => {
                    const baseTotal = +(Number(item.price) * Number(item.quantity)).toFixed(2);

                    // const finalTotal = typeof item.totalPrice === 'number' ? item.totalPrice : baseTotal;
                    // const hasAnyDiscountVisual =
                    //     finalTotal < baseTotal || item.discountedPrice != null || item.specialDiscountedPrice != null || item.secondPillowDiscountAmount != null;

                    const subtotalBeforePromo = typeof item.totalPrice === 'number' ? item.totalPrice : baseTotal;

                    const hasNonPromoDiscountVisual = !isPromoEligibleItem(item);

                    const canApplyPromoLocally = Boolean(appliedPromo?.code && isPromoEligibleItem(item));

                    const promoDiscountAmount = (() => {
                        if (!canApplyPromoLocally || !appliedPromo) return 0;
                        if (appliedPromo.discountType === 'percentage') {
                            const pct = Number(appliedPromo.discountValue) || 0;
                            if (pct <= 0) return 0;
                            return +((baseTotal * pct) / 100).toFixed(2);
                        }
                        if (appliedPromo.discountType === 'points') {
                            // const totalDiscount = Number(appliedPromo.discountValue) || 0;
                            // if (totalDiscount <= 0) return 0;
                            // const denom = baseTotalsSum > 0 ? baseTotalsSum : 0;
                            // if (denom <= 0) return 0;
                            // return +(totalDiscount * (baseTotal / denom)).toFixed(2);
                            const totalDiscount = Math.min(Number(appliedPromo.discountValue) || 0, promoEligibleBaseTotalsSum);
                            if (totalDiscount <= 0) return 0;
                            const denom = promoEligibleBaseTotalsSum > 0 ? promoEligibleBaseTotalsSum : 0;
                            if (denom <= 0) return 0;
                            return +(totalDiscount * (baseTotal / denom)).toFixed(2);
                        }
                        return 0;
                    })();

                    const finalTotal = canApplyPromoLocally ? Math.max(0, +(baseTotal - promoDiscountAmount).toFixed(2)) : subtotalBeforePromo;

                    const hasAnyDiscountVisual = hasNonPromoDiscountVisual || (canApplyPromoLocally && finalTotal < baseTotal);

                    const imageId = typeof item.image === 'string' ? item.image : '';
                    const imageSrc = imageId ? getImageUrl(imageId) : '';

                    const groupLine = normalizeGroupLine(item.group ?? undefined);

                    // const promoLine =
                    //     appliedPromo?.code && appliedPromo.discountType === 'percentage'
                    //         ? `${appliedPromo.discountValue}% по промокоду ${appliedPromo.code}`
                    //         : appliedPromo?.code && appliedPromo.discountType === 'points'
                    //           ? `${toMoney(appliedPromo.discountValue)} ₽ по промокоду ${appliedPromo.code}`
                    //           : null;
                    const promoLine =
                        canApplyPromoLocally && appliedPromo?.code && appliedPromo.discountType === 'percentage'
                            ? `${appliedPromo.discountValue}% по промокоду ${appliedPromo.code}`
                            : canApplyPromoLocally && appliedPromo?.code && appliedPromo.discountType === 'points'
                              ? `${toMoney(appliedPromo.discountValue)} ₽ по промокоду ${appliedPromo.code}`
                              : null;

                    const secondPillowLine =
                        typeof item.secondPillowAppliedQty === 'number' && item.secondPillowAppliedQty > 0
                            ? `${item.secondPillowAppliedQty} шт со скидкой −${item.secondPillowDiscountPercent ?? 50}%`
                            : null;

                    const fillerOrFabricName = pickFillerOrFabricName(item);

                    return (
                        <article className="cartItemsSection__item" key={item.productId} role="listitem">
                            <label className="cartItemsSection__select">
                                <input type="checkbox" checked={selectedSet.has(item.productId)} onChange={() => onToggleSelect(item.productId)} />
                                <span className="cartItemsSection__selectMark" aria-hidden="true" />
                                <span className="cartItemsSection__srOnly">Выбрать товар</span>
                            </label>

                            <div className="cartItemsSection__media">
                                {imageSrc ? (
                                    <img className="cartItemsSection__img" src={imageSrc} alt={item.name} />
                                ) : (
                                    <div className="cartItemsSection__imgStub" aria-hidden="true" />
                                )}
                            </div>

                            <div className="cartItemsSection__content">
                                <div className="cartItemsSection__left">
                                    <div className="cartItemsSection__nameBlock">
                                        <Link
                                            className="cartItemsSection__name"
                                            to={generatePath('/catalog/:categoryUrl/:id', {
                                                categoryUrl: item.categoryUrl,
                                                id: item.productId,
                                            })}
                                        >
                                            {item.name}
                                        </Link>

                                        <div className="cartItemsSection__detailsBox">
                                            {item.nameDescription ? <div className="cartItemsSection__meta">{item.nameDescription}</div> : null}
                                            {fillerOrFabricName ? <div className="cartItemsSection__meta">{fillerOrFabricName}</div> : null}
                                            {groupLine ? <div className="cartItemsSection__meta">{groupLine}</div> : null}
                                        </div>
                                    </div>
                                    <div className="cartItemsSection__icons">
                                        <IconButton onClick={() => onWish({ id: item.productId, isWished: item.isWished })} title="Избранное">
                                            <>
                                                <FavoriteIcon filled={item.isWished} />
                                            </>
                                        </IconButton>
                                        <IconButton onClick={() => onRemoveOne(item.productId)} title="Удалить">
                                            <>
                                                <TrashIcon />
                                            </>
                                        </IconButton>
                                    </div>
                                </div>

                                <div className="cartItemsSection__right">
                                    {secondPillowLine || promoLine ? (
                                        <div className="cartItemsSection__promoMetaBox">
                                            {secondPillowLine ? <div className="cartItemsSection__promoMeta">{secondPillowLine}</div> : null}
                                            {promoLine ? <div className="cartItemsSection__promoMeta">{promoLine}</div> : null}
                                        </div>
                                    ) : null}
                                    <div className="cartItemsSection__price">
                                        <span className={`cartItemsSection__priceNow ${hasAnyDiscountVisual ? 'cartItemsSection__priceNow--discount' : ''}`}>
                                            {toMoney(finalTotal)} ₽
                                        </span>

                                        {hasAnyDiscountVisual && (
                                            <span className="cartItemsSection__priceOld" aria-label="Цена без скидки">
                                                {toMoney(baseTotal)} ₽
                                            </span>
                                        )}
                                    </div>

                                    <div className="cartItemsSection__qty">
                                        <QuantityBlock id={item.productId} disableDecrement background="#f5f4ee" />
                                    </div>
                                </div>
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
};
